import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAlerts } from "../../contexts/AlertsContext";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import SETTINGS from "../../globals";
import EntityStats from "../../model/EntityStats";
import ChemiNovaEntityType from "../../model/enums/ChemiNovaEntityType";
import { IApiClientError } from "../../shared/ApiClient";
import APIEndPoints from "../../utils/APIEndPoints";
import AppIcons from "../../utils/AppIcons";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import ImgUtil from "../../utils/ImgUtil";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import ButtonInfo from "../common/button/ButtonInfo";
import ButtonType from "../common/button/ButtonType";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import Collection from "../../model/Collection";
import GroupingType from "../../model/enums/GroupingType";
import ReportButton from "../common/ReportButton";

function CollectionDetails() {
  // read the ID from the url
  const { id } = useParams();

  // reference to the AuthManager
  const authManager = useAuth();
  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<Collection>(Collection.EmptyInstance());
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // indicate if a refresh is necessary
  const [refreshNeeded, setRefreshNeeded] = useState(false);

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let collection: Collection | undefined = undefined;
        if (id) {
          collection = await apiClient?.get<Collection>({
            url: APIEndPoints.COLLECTIONS.ONE(parseInt(id)),
          });
          setData(collection);
        }
      } catch (error: any) {}
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id, refreshNeeded]);

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.COLLECTIONS.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Record deleted successfully",
        });
        navigate(SETTINGS.get_path({ section: AppSections.COLLECTIONS }));
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete this record",
          });
        }
      });
  };

  const removeAssetFromCollection = async (assetId: number) => {
    if (!id) {
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message: "Cannot access database records",
      });
    } else {
      // initialize the list of the items that will remain in this group
      data.id_chelements = [];
      if (data.chelements) {
        // filter out the element to be removed
        data.id_chelements = data?.chelements
          .filter((e) => e.id !== assetId)
          .map((e) => e.id!);
      }

      apiClient
        ?.put({
          url: APIEndPoints.COLLECTIONS.ONE(parseInt(id)),
          body: data,
        })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "Asset successfully removed from this collection.",
          });
          setRefreshNeeded(!refreshNeeded);
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            alertsManager.showAlertMessage({
              type: AlertMessageType.ERROR,
              message: "Unable to implement the remove action.",
            });
          }
        });
    }
  };

  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (data.id_user === authManager.getUserID() ||
      authManager.isAdmin());

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="details-page">
      <div className="details-header">
        {/* Image */}
        <div className="details-icon">
          <div>
            <img
              src={
                ImgUtil.display_icon(data.icon_path) ||
                process.env.REACT_APP_PUBLIC_URL +
                  "/resources/images/no-image.png"
              }
              alt={"Collection icon"}
            />
          </div>
        </div>

        {/* Info Section */}
        <div className="details-header-content">
          <div className="details-title">
            <h1>{data.name}</h1>
          </div>
          <div className="details-overview">
            <ButtonInfo className="span-3">
              {AppIcons.USER}
              created by: <span>{data.username}</span>
            </ButtonInfo>
            {/* Button to open modal */}
            <ButtonInfo>
              {AppIcons.CH_ELEMENT}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.CHELEMENT,
                )}
              </span>
              Cultural Heritage Assets
            </ButtonInfo>
          </div>
          
          <div className="flex items-start mt-5 gap-4">
            {canEdit && (
              <>
                <Button
                  label={"Delete Collection"}
                  purpose={ButtonAction.DELETE}
                  onClick={() => {
                    alertsManager.showDialogWindow({
                      type: DialogWindowType.CONFIRM,
                      message: "Are you sure you want to delete this collection?",
                      cancelLabel: "Cancel",
                      confirmLabel: "Confirm",
                      onConfirm: handleDelete,
                    });
                  }}
                />

                <Button
                  label={"Explore collection"}
                  purpose={ButtonAction.EXPLORE}
                  onClick={() => {
                    alertsManager.showGroupExplore({
                      groupID: parseInt(id!),
                      groupType: GroupingType.COLLECTION,
                      apiClient: apiClient,
                    });
                  }}
                />
              </> 
            )}

            <ReportButton
                  entityType="Collection"
                  entityName={data.name}
                  entityId={data.id || 0}
            />
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Collection details</h2>
          {canEdit && (
            <Button
              label="Edit information"
              purpose={ButtonAction.EDIT}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.COLLECTIONS,
                    id: data.id,
                    subsection: AppSubSections.EDIT,
                  }),
                )
              }
            />
          )}
        </div>
        <p className="details-description">
          {data.description ?? "No description provided."}
        </p>
      </div>

      {/* CHElements Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Cultural Heritage Assets in this collection</h2>
          {canEdit && (
            <Button
              label={"How to add assets to this collection?"}
              purpose={ButtonAction.INFO}
              type={ButtonType.ICON_TEXT}
              onClick={() => {
                alertsManager.showDialogWindow({
                  type: DialogWindowType.INFO,
                  message:
                    "Adding Cultural Heritate Assets to Sites, Collections and private Lists can be done only from the details page of each CH Asset.",
                  confirmLabel: "Ok",
                  onConfirm: () => {},
                });
              }}
            />
          )}
        </div>
        <div className="details-section-content">
          {(!data.chelements || data.chelements.length === 0) && (
            <div className="text-left">
              There are no Cultural Heritage Assets in this collection.
            </div>
          )}
          {data.chelements &&
            data.chelements.length > 0 &&
            data.chelements.map((e) => {
              return (
                <div
                  className="details-grid-element"
                  onClick={() => {
                    navigate(
                      SETTINGS.get_path({
                        section: AppSections.CHELEMENTS,
                        id: e.id,
                        subsection: AppSubSections.VIEW,
                      }),
                    );
                  }}
                >
                  <div className="details-grid-content p-0 flex flex-row">
                    <div className="details-grid-description grow">
                      {e.name}
                    </div>
                    {canEdit && (
                      <Button
                        type={ButtonType.ICON}
                        purpose={ButtonAction.DELETE}
                        label=""
                        onClick={() => {
                          alertsManager.showDialogWindow({
                            type: DialogWindowType.CONFIRM,
                            message:
                              "To add Cultural Heritate Assets to collections:",
                            instructions: [
                              "Search the CH Asset using any of the available functionalities.",
                              "Open the CH Asset details page",
                              "Click on the 'Add this asset to another Collection' button and select the desired collection.",
                            ],
                            cancelLabel: "Cancel",
                            confirmLabel: "Confirm",
                            onConfirm: () => removeAssetFromCollection(e.id!),
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default CollectionDetails;
